import { svgEl } from '@squadlab/kernel/svg';

/**
 * The two decorative glyphs the home link is drawn from. Both are built by DOM
 * construction (no `innerHTML`, ADR 0008) and both paint in `currentColor` so a
 * consuming service themes them by setting one colour, not by re-authoring SVG.
 *
 * They are `aria-hidden`: the link's `aria-label` is the accessible name, and a
 * decorative glyph that also announced itself would say everything twice.
 */

function root(size: number): SVGSVGElement {
  const svg = svgEl('svg', {
    viewBox: `0 0 ${size} ${size}`,
    width: size,
    height: size,
    fill: 'none',
    focusable: 'false',
    'aria-hidden': 'true',
  });
  return svg;
}

/**
 * The mothership, simplified for small sizes.
 *
 * The full silhouette (`services/command-center/identity/mothership.svg`) is a
 * five-ellipse saucer that turns to mush below ~32px. This is the same craft read
 * at 18px: hull, dome, mast — the three strokes that survive. It is a *mark*, not a
 * copy of the identity asset, which is why it lives in code beside the component
 * that draws it rather than as a copied file.
 */
export function mothershipMark(): SVGSVGElement {
  const svg = root(18);
  svg.classList.add('mark');
  // hull
  svg.append(
    svgEl('ellipse', {
      cx: 9,
      cy: 11,
      rx: 7.6,
      ry: 2.1,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.3,
    }),
  );
  // upper tier
  svg.append(
    svgEl('ellipse', {
      cx: 9,
      cy: 8.4,
      rx: 4.2,
      ry: 1.5,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.2,
    }),
  );
  // mast + beacon
  svg.append(
    svgEl('line', {
      x1: 9,
      y1: 6.9,
      x2: 9,
      y2: 4.4,
      stroke: 'currentColor',
      'stroke-width': 1.2,
      'stroke-linecap': 'round',
    }),
  );
  svg.append(svgEl('circle', { cx: 9, cy: 3.4, r: 1.15, fill: 'currentColor' }));
  return svg;
}

/** The "up and out of here" chevron that leads the label. */
export function backChevron(): SVGSVGElement {
  const svg = root(12);
  svg.classList.add('chevron');
  svg.append(
    svgEl('path', {
      d: 'M7.5 2.5 3 6.5l4.5 4',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.5,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }),
  );
  return svg;
}
